import React, { useContext, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import { orderAgainOrders } from "../../apollo/server";
import UserContext from "../../context/User";
import ConfigurationContext from "../../context/Configuration";
import { resizedImage } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../utils/designTokens";
import useReorder from "../Orders/useReorder";
import { SectionHead } from "./listUi";
import { formatIn } from "../../utils/restaurantClock";

const ORDER_AGAIN = gql`${orderAgainOrders}`;

const isDone = (o) => o.orderStatus === "DELIVERED" || o.orderStatus === "COMPLETED" || (o.orderStatus === "PICKED" && o.isPickedUp);

// What the customer paid, as My Orders adds it up.
const totalOf = (o) =>
  parseFloat(o.orderAmount || 0) +
  (o.isPickedUp ? 0 : parseFloat(o.deliveryCharges || 0)) +
  parseFloat(o.tipping || 0) +
  parseFloat(o.taxationAmount || 0) +
  parseFloat(o.serviceFee || 0);

function OrderCard({ order, currency, busy, onReorder }) {
  const photo = order.items.find((it) => it.image)?.image || order.restaurant.logo || order.restaurant.image;
  const summary = order.items.map((it) => `${it.quantity > 1 ? `${it.quantity}× ` : ""}${it.title}`).join(", ");
  const date = formatIn(order.createdAt, order.restaurant?.timezone, { month: "short", day: "numeric" });
  return (
    <Box
      component="li"
      sx={{
        listStyle: "none",
        // Photo · text · Reorder; on a phone Reorder drops under the text.
        display: "grid",
        gridTemplateColumns: { xs: "88px minmax(0, 1fr)", sm: "88px minmax(0, 1fr) auto" },
        gridTemplateAreas: { xs: '"photo text" "photo action"', sm: '"photo text action"' },
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
        p: 1.5,
        minWidth: 0,
        borderRadius: "18px",
        border: `1px solid ${C.border}`,
        bgcolor: C.bg,
      }}
    >
      <Box sx={{ gridArea: "photo", width: 88, height: 88, flexShrink: 0, borderRadius: "14px", overflow: "hidden", bgcolor: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
        {photo ? (
          <Box component="img" src={resizedImage(photo, 240)} alt="" loading="lazy" decoding="async" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <RestaurantRoundedIcon aria-hidden="true" />
        )}
      </Box>
      <Box sx={{ gridArea: "text", minWidth: 0 }}>
        <Box sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500, color: C.muted }}>{date}</Box>
        <Box component="h3" sx={{ m: 0, mt: 0.25, fontFamily: FONT_BODY, fontSize: 16, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {order.restaurant.name}
        </Box>
        {/* The items shorten; the total always shows. */}
        <Box sx={{ mt: 0.25, display: "flex", minWidth: 0, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{summary}</Box>
          <Box component="span" sx={{ flexShrink: 0 }}>&nbsp;· {currency}{totalOf(order).toFixed(2)}</Box>
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={() => onReorder(order)}
        disabled={busy}
        aria-busy={busy}
        aria-label={`Reorder from ${order.restaurant.name}`}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          gridArea: "action",
          justifySelf: "start",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 44,
          px: 2,
          borderRadius: RADIUS.pill,
          bgcolor: C.red,
          color: "#FFFFFF",
          fontFamily: FONT_BODY,
          fontSize: 14,
          fontWeight: 700,
          "&:hover:not(:disabled)": { bgcolor: C.redHover },
          "&:disabled": { opacity: 0.7, cursor: "default" },
        }}
      >
        {busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <ReplayRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />}
        Reorder
      </Box>
    </Box>
  );
}

/**
 * "Order again": the customer's most recent completed order from each of a
 * few restaurants. Reorder (components/Orders/useReorder.js) puts that
 * order's items in the cart and opens the restaurant, asking first if the
 * cart holds another restaurant's items.
 */
function OrderAgain({ onOpenRestaurant }) {
  const { isLoggedIn } = useContext(UserContext);
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const { data } = useQuery(ORDER_AGAIN, { variables: { limit: 12 }, skip: !isLoggedIn, fetchPolicy: "cache-first" });
  const { reorder, busyId, dialog } = useReorder({ onOpenRestaurant });

  const orders = useMemo(() => {
    const seen = new Set();
    return (data?.orders || [])
      .filter((o) => o.restaurant?._id && o.items?.length && isDone(o))
      .filter((o) => !seen.has(o.restaurant._id) && seen.add(o.restaurant._id))
      .slice(0, 4);
  }, [data]);

  if (!isLoggedIn || !orders.length) return null;

  return (
    <Box component="section" aria-labelledby="again-title">
      <SectionHead id="again-title" title="Order again" link={{ to: "/orders", label: "My orders" }} />
      <Box component="ul" sx={{ m: 0, p: 0, display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 380px), 1fr))" }}>
        {orders.map((o) => (
          <OrderCard key={o._id} order={o} currency={currency} busy={busyId === o._id} onReorder={reorder} />
        ))}
      </Box>
      {dialog}
    </Box>
  );
}

export default OrderAgain;
